import { useId, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface CollapsibleSectionProps {
  // Accepts a node, not just a string, so callers can render decorated titles
  // like "Evidence (5)" where the count is styled differently from the label.
  title: React.ReactNode;
  subtitle?: string;
  /** Rendered directly below `subtitle` in the section header (e.g. route with MapPin). */
  afterSubtitle?: React.ReactNode;
  defaultOpen?: boolean;
  /** When set, section is controlled by parent (e.g. programmatic open). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  rightAdornment?: React.ReactNode;
}

export default function CollapsibleSection({
  title,
  subtitle,
  afterSubtitle,
  defaultOpen = false,
  open: openControlled,
  onOpenChange,
  children,
  rightAdornment,
}: CollapsibleSectionProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = openControlled !== undefined;
  const open = isControlled ? Boolean(openControlled) : uncontrolledOpen;

  const setOpen = (next: boolean) => {
    if (isControlled) {
      onOpenChange?.(next);
    } else {
      setUncontrolledOpen(next);
    }
  };

  const contentId = useId();

  return (
    <div className="rounded-xl transition-shadow duration-200 hover:shadow-glow-success-ring-xl">
      <section className="bg-card/40 rounded-xl border border-border/50 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left hover:bg-card/40 transition-colors"
        aria-expanded={open}
        aria-controls={contentId}
      >
        <div className="flex items-start gap-2 min-w-0">
          {open ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            {subtitle ? <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div> : null}
            {afterSubtitle ? <div className="mt-1">{afterSubtitle}</div> : null}
          </div>
        </div>
        {rightAdornment ? <div className="flex-shrink-0 pt-0.5">{rightAdornment}</div> : null}
      </button>

      {open ? (
        <div
          id={contentId}
          key={String(open)}
          className="px-4 pb-4 zoe-snap-in"
        >
          {children}
        </div>
      ) : null}
      </section>
    </div>
  );
}
